import { ReactNode } from "react";
import {
  AgricultureOutlined,
  ApartmentOutlined,
  CategoryOutlined,
  DirectionsCarOutlined,
  FactoryOutlined,
  HomeOutlined,
  LocalShippingOutlined,
  MapOutlined,
  StorefrontOutlined,
  TwoWheelerOutlined,
  WarehouseOutlined,
  WorkOutline,
} from "@mui/icons-material";
import { GuaranteeVehicleDetailConfig } from "@interfaces/catalogs";
import { ContainerIcon, StethoscopeIcon } from "../iconosLucide";
import { TextFieldSelectIcon } from "../TextFieldSelectIcon";

/**
 * Ícono y descripción de cada detalle del catálogo, los del `IconSelect` del diseño (`INMUEBLES` y `PRENDARIOS` de
 * `TipoGarantia`); los íconos que MUI no tiene están en `iconosLucide`. El catálogo solo trae el nombre, así que se
 * reconoce por palabra clave y el orden importa ("Tractocamiones" antes que "Camión", "pickup" antes que "Auto").
 */
const DETAIL_PRESENTATION: Array<[RegExp, ReactNode, string]> = [
  // Inmuebles
  [/casa/i, <HomeOutlined key="casa" />, "Casa propia, con escrituras"],
  [/departamento/i, <ApartmentOutlined key="depto" />, "En condominio, con escrituras"],
  [/local|comercial/i, <StorefrontOutlined key="local" />, "Local o plaza comercial"],
  [/bodega|nave/i, <WarehouseOutlined key="bodega" />, "Almacén, nave o planta"],
  [/oficina/i, <WorkOutline key="oficina" />, "Oficina o consultorio"],
  [/terreno/i, <MapOutlined key="terreno" />, "Urbano, rústico o ejidal titulado"],
  // Bienes muebles
  [/moto/i, <TwoWheelerOutlined key="moto" />, "Trabajo, reparto y uso urbano"],
  [/tracto/i, <ContainerIcon key="tracto" />, "Tráiler, cajas, plataformas y tolvas"],
  [/agr[ií]cola/i, <AgricultureOutlined key="agricola" />, "Tractores, cosechadoras e implementos"],
  [/maquinaria|montacargas/i, <AgricultureOutlined key="maquinaria" />, "Construcción, montacargas y elevación"],
  [/producci[oó]n/i, <FactoryOutlined key="produccion" />, "Líneas, CNC, compresores y empaque"],
  [/m[eé]dico|tecnolog/i, <StethoscopeIcon key="medico" />, "Imagenología, quirófano, cómputo y servidores"],
  [/pickup|camioneta/i, <LocalShippingOutlined key="pickup" />, "Valuación con Libro Azul"],
  [/\bvans?\b|last mile/i, <LocalShippingOutlined key="van" />, "Carga ligera y reparto"],
  [/cami[oó]n|carga/i, <LocalShippingOutlined key="camion" />, "Torton, rabón, volteo y refrigerado"],
  [/auto|suv/i, <DirectionsCarOutlined key="auto" />, "Valuación con Libro Azul"],
];

const presentationOf = (name: string) => {
  const match = DETAIL_PRESENTATION.find(([pattern]) => pattern.test(name));
  return match ? { icon: match[1], sub: match[2] } : { icon: <CategoryOutlined /> };
};

interface Props {
  value: string;
  details: GuaranteeVehicleDetailConfig[];
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled: boolean;
  testId: string;
}

/** Select de un detalle del catálogo de garantías (tipo de inmueble o prendario), cada opción con ícono y descripción. */
export const TextFieldSelectDetalle = ({ details, ...rest }: Props) => (
  <TextFieldSelectIcon
    {...rest}
    compact
    options={details.map((detail) => ({
      value: detail.guarantee_type_detail_id,
      label: detail.guarantee_type_detail.name,
      ...presentationOf(detail.guarantee_type_detail.name),
    }))}
  />
);
